import React, { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'react-toastify';
import api from '../../services/api';
import { confirmDelete } from '../../utils/confirm';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

function TopicModal({ courseId, tenantId, topic, onClose, onSaved }) {
  const isEdit = !!topic;
  const [form, setForm] = useState({
    topicName: topic?.TopicName || '',
    description: topic?.Description || '',
    sequenceNo: topic?.SequenceNo != null ? topic.SequenceNo : '',
    isActive: topic ? topic.IsActive : true,
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.topicName.trim()) return toast.error('Topic Name is required');

    setSaving(true);
    try {
      const payload = {
        courseId,
        tenantId,
        topicName: form.topicName.trim(),
        description: form.description.trim() || null,
        sequenceNo: form.sequenceNo !== '' ? parseFloat(form.sequenceNo) : null,
        isActive: form.isActive,
      };
      if (isEdit) {
        await api.put(`/courses/topics/${topic.TopicID}`, payload);
        toast.success('Topic updated');
      } else {
        await api.post('/courses/topics', payload);
        toast.success('Topic created');
      }
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <h2>{isEdit ? 'Edit Topic' : 'Add Topic'}</h2>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="required-label">Topic Name</label>
            <input type="text" placeholder="e.g., Introduction to Algorithms" value={form.topicName} onChange={(e) => setForm({ ...form, topicName: e.target.value })} autoFocus />
          </div>
          <div className="form-group">
            <label>Description</label>
            <textarea className="form-textarea" placeholder="Brief description of this topic" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Sequence No</label>
              <input type="number" step="0.1" placeholder="e.g., 1 or 1.2" value={form.sequenceNo} onChange={(e) => setForm({ ...form, sequenceNo: e.target.value })} />
            </div>
            <div className="form-group">
              <label className="checkbox-inline" style={{ marginTop: 28 }}>
                <input type="checkbox" checked={form.isActive} onChange={(e) => setForm({ ...form, isActive: e.target.checked })} />
                Is Active
              </label>
            </div>
          </div>
          <div className="form-actions">
            <button type="button" className="btn btn-cancel-text" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-save" disabled={saving}>{saving ? 'Saving...' : isEdit ? 'Update' : 'Create'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function TopicImportModal({ tenantId, onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);

  const handleDownloadTemplate = () => {
    const token = localStorage.getItem('qposm_token');
    fetch(`${API_BASE}/courses/template/topics`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob())
      .then(blob => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'Course_Topics_Import_Template.xlsx';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      });
  };

  const handleImport = async () => {
    if (!file) return toast.error('Please select a file');
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('tenantId', tenantId);
      const res = await api.post('/courses/import/topics', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setResult(res.data);
      if (res.data.imported > 0) toast.success(`${res.data.imported} topic(s) imported`);
      if (res.data.discrepancies?.length > 0) toast.warn(`${res.data.discrepancies.length} discrepancy(ies) found`);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  const handleDownloadDiscrepancies = async () => {
    try {
      const res = await api.post('/courses/discrepancies/topics', { discrepancies: result.discrepancies }, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = 'Topic_Import_Discrepancies.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Failed to download discrepancies');
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-lg" onClick={(e) => e.stopPropagation()}>
        <h2>Import Course Topics</h2>

        {!result ? (
          <>
            <div className="import-steps">
              <div className="import-step">
                <span className="import-step-num">1</span>
                <div>
                  <strong>Download Template</strong>
                  <p style={{ fontSize: 13, color: '#616161', margin: '4px 0' }}>Template columns: Course Code, Topic Name, Description, Sequence No, Is Active.</p>
                  <button className="btn btn-outline btn-sm" onClick={handleDownloadTemplate}>Download Template</button>
                </div>
              </div>
              <div className="import-step">
                <span className="import-step-num">2</span>
                <div>
                  <strong>Upload File</strong>
                  <p style={{ fontSize: 13, color: '#616161', margin: '4px 0' }}>Use Course Code to map topics to existing courses. Courses must exist before importing topics.</p>
                  <div className="file-upload-area">
                    <input type="file" accept=".xlsx,.xls" className="file-input" id="import-topic-file" onChange={(e) => setFile(e.target.files[0])} />
                    <label htmlFor="import-topic-file" className="file-upload-label">
                      <span className="file-upload-icon">📎</span>
                      {file ? file.name : 'Choose Excel file (.xlsx)'}
                    </label>
                  </div>
                </div>
              </div>
            </div>
            <div className="import-note">
              <strong>Note:</strong> Course Code must match an existing course. Duplicate topic names within the same course will be skipped.
            </div>
            <div className="form-actions">
              <button type="button" className="btn btn-cancel-text" onClick={onClose}>Cancel</button>
              <button className="btn btn-save" onClick={handleImport} disabled={!file || importing}>
                {importing ? 'Importing...' : 'Import'}
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="import-result-summary">
              <div className="import-stat import-stat-success">
                <span className="import-stat-num">{result.imported}</span>
                <span>Imported</span>
              </div>
              <div className={`import-stat ${result.discrepancies?.length > 0 ? 'import-stat-error' : 'import-stat-success'}`}>
                <span className="import-stat-num">{result.discrepancies?.length || 0}</span>
                <span>Discrepancies</span>
              </div>
            </div>

            {result.discrepancies?.length > 0 && (
              <>
                <div style={{ maxHeight: 250, overflowY: 'auto', marginBottom: 16 }}>
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Row</th>
                        <th>Course Code</th>
                        <th>Topic Name</th>
                        <th>Reason</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.discrepancies.map((d, i) => (
                        <tr key={i}>
                          <td>{d.row}</td>
                          <td>{d.courseCode}</td>
                          <td style={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.topicName}</td>
                          <td style={{ color: '#c62828', fontSize: 13 }}>{d.reason}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <button className="btn btn-outline btn-sm" onClick={handleDownloadDiscrepancies} style={{ marginBottom: 16 }}>
                  Download Discrepancies
                </button>
              </>
            )}

            <div className="form-actions">
              <button className="btn btn-save" onClick={() => { onDone(); onClose(); }}>Done</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function CourseTopicsTab({ tenantId }) {
  const [courses, setCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState('');
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 0 });
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [editTopic, setEditTopic] = useState(null);
  const pageSize = 50;
  const searchTimer = useRef();

  // Load courses for dropdown
  useEffect(() => {
    if (!tenantId) return;
    api.get(`/courses/dropdown/${tenantId}`)
      .then(res => { if (res.data.success) setCourses(res.data.data); })
      .catch(() => toast.error('Failed to load courses'));
  }, [tenantId]);

  const loadTopics = useCallback(async (p = page, s = search) => {
    if (!selectedCourse) { setTopics([]); setPagination({ total: 0, totalPages: 0 }); return; }
    try {
      setLoading(true);
      const res = await api.get(`/courses/topics/${selectedCourse}`, { params: { page: p, pageSize, search: s } });
      if (res.data.success) {
        setTopics(res.data.data);
        setPagination(res.data.pagination);
      }
    } catch {
      toast.error('Failed to load topics');
    } finally {
      setLoading(false);
    }
  }, [selectedCourse, page, search]);

  useEffect(() => { loadTopics(); }, [loadTopics]);

  const handleCourseChange = (val) => {
    setSelectedCourse(val);
    setPage(1);
    setSearch('');
    setSearchInput('');
  };

  const handleSearchChange = (val) => {
    setSearchInput(val);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setSearch(val);
      setPage(1);
    }, 400);
  };

  const handleDelete = async (topicId) => {
    if (!(await confirmDelete('Topic'))) return;
    try {
      await api.delete(`/courses/topics/${topicId}`);
      toast.success('Topic deleted');
      loadTopics();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete');
    }
  };

  const handleExport = async () => {
    if (!selectedCourse) return toast.error('Select a course first');
    try {
      const token = localStorage.getItem('qposm_token');
      const res = await fetch(`${API_BASE}/courses/export/topics/${selectedCourse}`, { headers: { Authorization: `Bearer ${token}` } });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'Course_Topics_Export.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Export failed');
    }
  };

  return (
    <>
      <div className="page-card-header" style={{ margin: 0, paddingBottom: 16, borderBottom: '1px solid #e0e0e0' }}>
        <h3>Course Topics {selectedCourse && pagination.total > 0 && <span style={{ fontWeight: 400, fontSize: 14, color: '#616161' }}>({pagination.total})</span>}</h3>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-outline btn-sm" onClick={handleExport} disabled={!selectedCourse} title="Export to Excel">Export</button>
          <button className="btn btn-outline btn-sm" onClick={() => setShowImport(true)}>Import</button>
          <button className="btn btn-add" onClick={() => { setEditTopic(null); setShowModal(true); }} disabled={!selectedCourse} title={!selectedCourse ? 'Select a course first' : ''}>
            + Add Topic
          </button>
        </div>
      </div>

      {/* Course selector + search */}
      <div className="user-filters" style={{ marginTop: 16 }}>
        <select className="filter-select" value={selectedCourse} onChange={(e) => handleCourseChange(e.target.value)} style={{ minWidth: 320 }}>
          <option value="">-- Select Course --</option>
          {courses.map((c) => (
            <option key={c.CourseID} value={c.CourseID}>{c.CourseCode} - {c.CourseName}</option>
          ))}
        </select>
        {selectedCourse && (
          <div className="search-box">
            <span className="search-icon">🔍</span>
            <input type="text" placeholder="Search topics..." value={searchInput} onChange={(e) => handleSearchChange(e.target.value)} />
          </div>
        )}
      </div>

      {!selectedCourse ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>Select a course to view and manage its topics.</div>
      ) : loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>Loading...</div>
      ) : topics.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
          {search ? 'No topics match your search.' : 'No topics found for this course. Add topics manually or import from Excel.'}
        </div>
      ) : (
        <>
          <table className="data-table" style={{ marginTop: 16 }}>
            <thead>
              <tr>
                <th style={{ width: 60 }}>Seq</th>
                <th>Topic Name</th>
                <th>Description</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {topics.map((t) => (
                <tr key={t.TopicID}>
                  <td style={{ color: '#616161', fontWeight: 600 }}>{t.SequenceNo ?? '-'}</td>
                  <td><strong>{t.TopicName}</strong></td>
                  <td style={{ color: '#616161', maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.Description || '-'}</td>
                  <td>
                    <span className={`badge ${t.IsActive ? 'badge-active' : 'badge-inactive'}`}>
                      {t.IsActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button className="action-btn action-edit" title="Edit" onClick={() => { setEditTopic(t); setShowModal(true); }}>✏</button>
                      <button className="action-btn action-delete" title="Delete" onClick={() => handleDelete(t.TopicID)}>🗑</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {pagination.totalPages > 1 && (
            <div className="pagination-bar">
              <button className="btn btn-outline btn-sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Previous</button>
              <span className="pagination-info">Page {page} of {pagination.totalPages}</span>
              <button className="btn btn-outline btn-sm" disabled={page >= pagination.totalPages} onClick={() => setPage(p => p + 1)}>Next</button>
            </div>
          )}
        </>
      )}

      {showModal && selectedCourse && (
        <TopicModal
          courseId={parseInt(selectedCourse)}
          tenantId={tenantId}
          topic={editTopic}
          onClose={() => { setShowModal(false); setEditTopic(null); }}
          onSaved={() => { setShowModal(false); setEditTopic(null); loadTopics(); }}
        />
      )}
      {showImport && (
        <TopicImportModal
          tenantId={tenantId}
          onClose={() => setShowImport(false)}
          onDone={() => loadTopics(1, search)}
        />
      )}
    </>
  );
}

export default CourseTopicsTab;
